import { hideContextMenu, showContextMenu } from "@blueprintjs/popover2";
import React, { useCallback, useEffect, useState } from "react";

import { usePlayContext } from "../../pages";
import { Sport } from "../../redux/slices";
import { Action, ActionLineType } from "../../types";
import { createNewAction } from "../../utils";
import { BasketballSelectActionMenu } from "./basketball-select-action-menu";
import { HockeySelectActionMenu } from "./hockey-select-action-menu";
import { SoccerSelectActionMenu } from "./soccer-select-action-menu";

export type ActionTypeFromPlayer = "PLAYER_WITH_POSSESSION" | "PLAYER_WITHOUT_POSSESSION" | null;

export interface SelectActionMenu {
  action: Pick<Action, "pos" | "midPoints" | "endPos">;
  actionTypeFromPlayer?: ActionTypeFromPlayer;
  menuPos: { x: number; y: number };
  cleanup?: () => void;
}

interface HandleClickNewAction extends Omit<SelectActionMenu, "menuPos"> {
  layerType: ActionLineType;
}

export const useSelectActionMenu = () => {
  const [selectActionMenu, setSelectActionMenu] = useState<SelectActionMenu | null>(null);

  const openSelectActionMenu = (menu: SelectActionMenu) => setSelectActionMenu(menu);
  const closeSelectActionMenu = () => setSelectActionMenu(null);

  const { frameStencilsApi, playStateApi } = usePlayContext();
  const { addStencil } = frameStencilsApi;
  const { playState } = playStateApi;
  const { sport } = playState;

  const addAction = useCallback(
    ({ layerType, action, cleanup }: HandleClickNewAction) => {
      const newAction = createNewAction({
        layerType,
        pos: action.pos,
        midPoints: action.midPoints,
        endPos: action.endPos,
      });
      addStencil({ stencil: newAction });
      closeSelectActionMenu();
      cleanup?.();
    },
    [addStencil]
  );

  const cancelAction = useCallback(({ cleanup }: Pick<SelectActionMenu, "cleanup">) => {
    closeSelectActionMenu();
    cleanup?.();
  }, []);

  useEffect(() => {
    if (selectActionMenu) {
      const { menuPos, actionTypeFromPlayer, action, cleanup } = selectActionMenu;

      showContextMenu({
        targetOffset: { left: menuPos.x, top: menuPos.y },
        content: (() => {
          switch (sport) {
            case Sport.BASKETBALL:
              return (
                <BasketballSelectActionMenu
                  handleClickAction={({ layerType }) => addAction({ layerType, action, cleanup })}
                  handleCancel={() => cancelAction({ cleanup })}
                />
              );

            case Sport.HOCKEY:
              return (
                <HockeySelectActionMenu
                  handleClickAction={({ layerType }) => addAction({ layerType, action, cleanup })}
                  handleCancel={() => cancelAction({ cleanup })}
                />
              );

            case Sport.SOCCER:
              return (
                <SoccerSelectActionMenu
                  actionTypeFromPlayer={actionTypeFromPlayer}
                  handleClickAction={({ layerType }) => addAction({ layerType, action, cleanup })}
                  handleCancel={() => cancelAction({ cleanup })}
                />
              );
          }
        })(),
        onClose: () => {
          cancelAction({ cleanup });
        },
      });
    } else {
      hideContextMenu();
    }
  }, [selectActionMenu, sport, addAction, cancelAction]);

  return { selectActionMenu, openSelectActionMenu, closeSelectActionMenu };
};
